import { ArrayNotEmpty, IsNotEmptyObject, Matches, Max, Min, ValidateIf } from 'class-validator';
import has from 'lodash/has';
import reject from 'lodash/reject';

import Actions, { actionFromDto, actionsFromDto, ActionType, invalidActionsForTrigger } from './actions';
import Condition, {
  AttributeType,
  conditionFromDto,
  conditionsFromDto,
  ConditionType,
  invalidConditionsForTrigger,
} from './condition';
import createCollectionResource from 'src/actions/shared/createResource';
import { matchesError } from 'src/models/DomainError';
import Trigger, { AgentStatusType, triggerFromDto, TriggerType } from './trigger';

export enum RuleStatus {
  Mutable = 'MUTABLE',
  Frozen = 'FROZEN',
}

export class Rule {
  id: string;
  @Matches(/\S/, { message: 'Please enter a name' })
  name: string;
  disabled: boolean;
  @ValidateIf(o => o.id != null && o.trigger.type != TriggerType.SmartMatch) // don't validate if new rule or smart match rule
  @Min(1, { message: 'Minimum allowed value is 1' })
  @Max(Number.MAX_SAFE_INTEGER)
  order?: number;
  @IsNotEmptyObject({ nullable: false }, { message: 'Please select at least one action' })
  actions: Actions;
  @ValidateIf(o => o.trigger.type == TriggerType.SmartMatch)
  @ArrayNotEmpty({ message: 'Please select at least one condition' })
  conditions: Condition[];
  status: RuleStatus;
  trigger: Trigger;
  updatedAt?: string;
  _version: number;

  static get propertyDtoConverters() {
    return {
      actions: actionsFromDto,
      conditions: conditionsFromDto,
      trigger: triggerFromDto,
    };
  }

  constructor(attr = {} as any) {
    for (const prop in attr) {
      if (has(Rule.propertyDtoConverters, prop)) {
        const fromDto = Rule.propertyDtoConverters[prop];
        this[prop] = fromDto(attr[prop]);
      } else {
        this[prop] = attr[prop];
      }
    }
  }

  toggleDisabled() {
    this.disabled = !this.disabled;
  }

  setName(name) {
    this.name = name;
  }

  setOrder(order) {
    this.order = order;
  }

  setTrigger(type: TriggerType, value?: number | AgentStatusType) {
    this.trigger = triggerFromDto({ type, value });
    if (type in invalidActionsForTrigger) {
      invalidActionsForTrigger[type].forEach(action => delete this.actions[action]);
    }
    if (type in invalidConditionsForTrigger) {
      invalidConditionsForTrigger[type].forEach(type => {
        this.conditions = reject(this.conditions, { type });
      });
    }
  }

  updateTrigger(trigger: Trigger) {
    this.trigger = trigger;
  }

  addCondition(type: ConditionType, name?: string, attrType?: AttributeType) {
    const condition = conditionFromDto({ type, name, attrType });
    this.conditions.push(condition);
  }

  updateCondition(condition: Condition, index: number) {
    this.conditions[index] = condition;
  }

  deleteCondition(index: number) {
    this.conditions.splice(index, 1);
  }

  addAction(type: ActionType) {
    const action = actionFromDto({ type });
    this.actions[type as string] = action;
  }

  updateActionValue(key: ActionType, value: any) {
    this.actions[key as string] = value;
  }

  deleteAction(key: ActionType) {
    delete this.actions[key as string];
  }

  toJs() {
    const js = {
      ...this,
      actions: JSON.parse(JSON.stringify(this.actions)),
      conditions: this.conditions.map(c => {
        return typeof (c as any).toJs === 'function' ? (c as any).toJs() : JSON.parse(JSON.stringify(c));
      }),
      trigger: this.trigger.toJs(),
    };

    // the TS class automatically sets missing fields to undefined
    // ensure we send the json with proper fields omitted for new rules
    ['id', 'order'].forEach(field => js[field] === undefined && delete js[field]);

    return js;
  }
}

const rulesResource = createCollectionResource<Rule>({
  name: 'rules',
  endpoint: 'v2/orgs/:orgId/rules/:id',
  type: Rule,
  observerCreator: ({ actions }) => {
    return ({ dispatch }) => {
      return {
        onBroadcast(item) {
          dispatch(actions.broadcast(item));
        },
      };
    };
  },
  options: {
    toastMessages: {
      onAdd: rule => `Added "${rule.name}"`,
      onUpdate: rule => `Updated rule "${rule.name}"`,
    },
    getErrorMessage: err => {
      if (matchesError(err, { attr: 'actions.sendReply', code: 'invalid' })) {
        switch (err.meta.variableErrorType) {
          case 'AGENT_VARIABLE':
            return `The answer cannot use an Agent Variable [${err.meta.variableName}], please select a different answer.`;
          case 'NO_DEFAULT_VALUE':
            return `The answer uses a Variable [${err.meta.variableName}] that doesn't have a default, please set the default value or select a different answer.`;
        }
      } else if (matchesError(err, { attr: 'actions.sendReply.snippetLanguage', code: 'not_exist' })) {
        return `The answer doesn't contain a language you selected, please select a different language or answer.`;
      } else if (matchesError(err, { attr: 'actions.sendReply.snippetId', code: 'not_exist' })) {
        return 'The answer does not exist, please select a different answer.';
      }
      return err.detail;
    },
  },
});

export default rulesResource;
